// MM-UI / leg-format — one renderer for every (market, pick) shape the
// engine emits. Two encoding conventions live side-by-side in pb_selections:
//   (a) line/side baked into `market`  → market=total_goals_under_3_5, pick=under
//   (b) line/side carried by `pick`    → market=ou,                    pick=under_3_5
// formatLeg() in market-format.ts handles a subset. This module is the
// complete map across every family the WIDEN-1..6 work added — so that no
// user ever again sees a bare "Under" or "home_plus_0_5".
//
// Pure & deterministic. No I/O, no DB, no fabrication — when an input is
// genuinely undecodable it falls through to a clean Title-Case fallback,
// never invents a line number.
//
// Doctrine (CLAUDE.md): outputs only. The label may name the team (when
// LegContext is supplied) and the market family in plain English; it
// MUST NOT reveal model internals, edge sizes, CLV, or methodology.

import { formatLeg, type LegContext } from "./market-format.ts";

export type { LegContext };

// "2_5" → "2.5" · "0_5" → "0.5" · "1" → "1"
function dec(line: string | null | undefined): string {
  return String(line ?? "").replace(/_/g, ".");
}

// Capitalise first letter only — proper nouns and team names stay intact.
function cap(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Side token → "Home" / "Away" / "Draw" / null (case-insensitive).
function sideToken(s: string): "home" | "away" | "draw" | null {
  const t = s.trim().toLowerCase();
  if (t === "home" || t === "h" || t === "1") return "home";
  if (t === "away" || t === "a" || t === "2") return "away";
  if (t === "draw" || t === "x" || t === "tie") return "draw";
  return null;
}

function teamFor(side: "home" | "away" | "draw" | null, ctx?: LegContext): string | null {
  if (!ctx || !side) return null;
  if (side === "home" && ctx.home_team) return ctx.home_team;
  if (side === "away" && ctx.away_team) return ctx.away_team;
  return null;
}

function sideLabel(side: "home" | "away" | "draw" | null, ctx?: LegContext): string {
  return teamFor(side, ctx) ?? (side === "home" ? "Home" : side === "away" ? "Away" : "Draw");
}

// "home_or_draw" / "1x" / "12" / "x2" → ["Home", "Draw"]
function dcPair(token: string, ctx?: LegContext): string | null {
  const t = token.trim().toLowerCase().replace(/[\s_-]+/g, "");
  // Symbolic forms first
  if (t === "1x" || t === "homedraw" || t === "homeordraw") {
    return `${sideLabel("home", ctx)} or Draw`;
  }
  if (t === "x2" || t === "drawaway" || t === "drawor away" || t === "awayordraw" || t === "draworaway") {
    return `Draw or ${sideLabel("away", ctx)}`;
  }
  if (t === "12" || t === "homeaway" || t === "homeoraway") {
    return `${sideLabel("home", ctx)} or ${sideLabel("away", ctx)}`;
  }
  return null;
}

/**
 * Render a complete, user-facing selection line from a (market, pick) pair.
 *
 *   renderLegSelection("total_goals_under_3_5", "under")          → "Under 3.5 Goals"
 *   renderLegSelection("ou", "under_3_5")                         → "Under 3.5 Goals"
 *   renderLegSelection("team_total_home_over_0_5", "over", ctx)   → "Arsenal Team Total — Over 0.5"
 *   renderLegSelection("asian_handicap", "home_plus_0_5", ctx)    → "Arsenal +0.5 (AH)"
 *   renderLegSelection("first_half_total_goals", "over_0_5")      → "1st Half — Over 0.5 Goals"
 *   renderLegSelection("first_half_winner", "away", ctx)          → "Chelsea (1st Half)"
 *   renderLegSelection("goal_bounds_2_plus", "yes")               → "2+ Goals in Match"
 *   renderLegSelection("combo_home_or_btts_yes", null, ctx)       → "Arsenal Win or BTTS Yes"
 *   renderLegSelection("to_qualify_home", "home", ctx)            → "Arsenal to Qualify"
 *   renderLegSelection("extra_time_yes", "yes")                   → "Match Goes to Extra Time"
 *   renderLegSelection("penalties_no", "no")                      → "No Penalty Shootout"
 *   renderLegSelection("both_halves_over_0_5", "yes")             → "Goal in Both Halves"
 *   renderLegSelection("first_half_corners_over_3_5", "over")     → "1st Half Corners — Over 3.5"
 *   renderLegSelection("home_corners_over_4_5", "over", ctx)      → "Arsenal Corners — Over 4.5"
 */
export function renderLegSelection(
  market: string | null | undefined,
  pick: string | null | undefined,
  ctx?: LegContext,
): string {
  const mk = String(market ?? "").trim().toLowerCase();
  const pk = String(pick ?? "").trim().toLowerCase();
  if (!mk && !pk) return "—";

  let m: RegExpMatchArray | null;

  // ─── Tournament-context (WIDEN-5) ────────────────────────────
  if ((m = mk.match(/^to_qualify_(home|away)$/))) {
    return `${sideLabel(m[1] as "home" | "away", ctx)} to Qualify`;
  }
  if ((m = mk.match(/^to_win_match_(home|away)$/))) {
    return `${sideLabel(m[1] as "home" | "away", ctx)} to Win (incl. ET / Pens)`;
  }
  if (mk === "extra_time_yes") return "Match Goes to Extra Time";
  if (mk === "extra_time_no") return "No Extra Time";
  if (mk === "penalties_yes") return "Decided by Penalty Shootout";
  if (mk === "penalties_no") return "No Penalty Shootout";

  // ─── OR combos (WIDEN-4) ─────────────────────────────────────
  // canonical: combo_{home|away|draw}_or_{over_2_5|under_2_5|btts_yes|btts_no}
  if ((m = mk.match(/^combo_(home|away|draw)_or_(over_2_5|under_2_5|btts_yes|btts_no)$/))) {
    const first = m[1] === "draw" ? "Draw" : `${sideLabel(m[1] as "home" | "away", ctx)} Win`;
    const tail = m[2]!;
    let second = "";
    if (tail === "over_2_5") second = "Over 2.5 Goals";
    else if (tail === "under_2_5") second = "Under 2.5 Goals";
    else if (tail === "btts_yes") second = "BTTS Yes";
    else if (tail === "btts_no") second = "BTTS No";
    return `${first} or ${second}`;
  }

  // ─── 1X2 + AND combos (M4) ───────────────────────────────────
  // canonical: combo_1x2_{home|draw|away}_and_total_{over|under}_{line}
  if ((m = mk.match(/^combo_1x2_(home|draw|away)_and_total_(over|under)_([0-9_]+)$/))) {
    const first = m[1] === "draw" ? "Draw" : `${sideLabel(m[1] as "home" | "away", ctx)} Win`;
    const dir = m[2] === "over" ? "Over" : "Under";
    return `${first} & ${dir} ${dec(m[3]!)} Goals`;
  }
  // canonical: combo_1x2_{home|draw|away}_and_btts_{yes|no}
  if ((m = mk.match(/^combo_1x2_(home|draw|away)_and_btts_(yes|no)$/))) {
    const first = m[1] === "draw" ? "Draw" : `${sideLabel(m[1] as "home" | "away", ctx)} Win`;
    return `${first} & BTTS ${m[2] === "yes" ? "Yes" : "No"}`;
  }

  // ─── Both halves (WIDEN-6) ───────────────────────────────────
  if ((m = mk.match(/^both_halves_(over|under)_([0-9_]+)$/))) {
    const dir = m[1] === "over" ? "Over" : "Under";
    return `${dir} ${dec(m[2]!)} Goals in Both Halves`;
  }

  // ─── Corners by half (WIDEN-6) ───────────────────────────────
  if ((m = mk.match(/^first_half_corners_(over|under)_([0-9_]+)$/))) {
    return `1st Half Corners — ${cap(m[1]!)} ${dec(m[2]!)}`;
  }
  if ((m = mk.match(/^second_half_corners_(over|under)_([0-9_]+)$/))) {
    return `2nd Half Corners — ${cap(m[1]!)} ${dec(m[2]!)}`;
  }
  // Team-total corners
  if ((m = mk.match(/^(home|away)_corners_(over|under)_([0-9_]+)$/))) {
    const side = m[1] as "home" | "away";
    const team = teamFor(side, ctx) ?? (side === "home" ? "Home" : "Away");
    return `${team} Corners — ${cap(m[2]!)} ${dec(m[3]!)}`;
  }
  // Total corners (WIDEN-1)
  if ((m = mk.match(/^total_corners_(over|under)_([0-9_]+)$/))) {
    return `${cap(m[1]!)} ${dec(m[2]!)} Corners`;
  }
  if (mk === "total_corners") {
    // Pick carries variant: "over_9_5" etc.
    if ((m = pk.match(/^(over|under)_([0-9_]+)$/))) {
      return `${cap(m[1]!)} ${dec(m[2]!)} Corners`;
    }
  }

  // ─── First / Second half totals ──────────────────────────────
  if ((m = mk.match(/^(first|second)_half_total_goals_(over|under)_([0-9_]+)$/))) {
    const half = m[1] === "first" ? "1st" : "2nd";
    return `${half} Half — ${cap(m[2]!)} ${dec(m[3]!)} Goals`;
  }
  if (mk === "first_half_total_goals" || mk === "second_half_total_goals") {
    const half = mk.startsWith("first") ? "1st" : "2nd";
    if ((m = pk.match(/^(over|under)_([0-9_]+)$/))) {
      return `${half} Half — ${cap(m[1]!)} ${dec(m[2]!)} Goals`;
    }
  }

  // ─── First / Second half winner ──────────────────────────────
  if ((m = mk.match(/^(first|second)_half_winner_(home|draw|away)$/))) {
    const half = m[1] === "first" ? "1st" : "2nd";
    const side = m[2] as "home" | "draw" | "away";
    if (side === "draw") return `Draw (${half} Half)`;
    return `${sideLabel(side, ctx)} (${half} Half)`;
  }
  if (mk === "first_half_winner" || mk === "second_half_winner") {
    const half = mk.startsWith("first") ? "1st" : "2nd";
    const side = sideToken(pk);
    if (side === "draw") return `Draw (${half} Half)`;
    if (side) return `${sideLabel(side, ctx)} (${half} Half)`;
  }

  // ─── First / Second half double-chance (WIDEN-1) ─────────────
  if ((m = mk.match(/^(first|second)_half_dc_(home_or_draw|home_or_away|away_or_draw)$/))) {
    const half = m[1] === "first" ? "1st" : "2nd";
    const pair = m[2]!;
    let text = "";
    if (pair === "home_or_draw") text = `${sideLabel("home", ctx)} or Draw`;
    else if (pair === "home_or_away") text = `${sideLabel("home", ctx)} or ${sideLabel("away", ctx)}`;
    else if (pair === "away_or_draw") text = `Draw or ${sideLabel("away", ctx)}`;
    return `${text} (${half} Half)`;
  }
  if (mk === "first_half_dc" || mk === "second_half_dc") {
    const half = mk.startsWith("first") ? "1st" : "2nd";
    const pair = dcPair(pk, ctx);
    if (pair) return `${pair} (${half} Half)`;
  }

  // ─── Team-total goals (M-series) ─────────────────────────────
  if ((m = mk.match(/^team_(?:total_)?(?:goals_)?(home|away)(?:_team)?_(over|under)_([0-9_]+)$/))) {
    const side = m[1] as "home" | "away";
    const team = teamFor(side, ctx) ?? (side === "home" ? "Home" : "Away");
    return `${team} Team Total — ${cap(m[2]!)} ${dec(m[3]!)}`;
  }
  if ((m = mk.match(/^team_total_(home|away)_(over|under)_([0-9_]+)$/))) {
    const side = m[1] as "home" | "away";
    const team = teamFor(side, ctx) ?? (side === "home" ? "Home" : "Away");
    return `${team} Team Total — ${cap(m[2]!)} ${dec(m[3]!)}`;
  }
  if ((m = mk.match(/^team_goals_(home|away)_(over|under)_([0-9_]+)$/))) {
    const side = m[1] as "home" | "away";
    const team = teamFor(side, ctx) ?? (side === "home" ? "Home" : "Away");
    return `${team} Team Total — ${cap(m[2]!)} ${dec(m[3]!)}`;
  }

  // ─── Goal-bounds (N to M+) ───────────────────────────────────
  if ((m = mk.match(/^goal_bounds_([0-9]+)_plus$/))) {
    return `${m[1]}+ Goals in Match`;
  }
  if ((m = mk.match(/^goal_bounds_([0-9]+)_to_([0-9]+)$/))) {
    return `${m[1]}–${m[2]} Goals in Match`;
  }

  // ─── Draw No Bet ─────────────────────────────────────────────
  if ((m = mk.match(/^draw_no_bet_(home|away)$/))) {
    return `${sideLabel(m[1] as "home" | "away", ctx)} (Draw No Bet)`;
  }
  if (mk === "draw_no_bet") {
    const side = sideToken(pk);
    if (side === "home" || side === "away") return `${sideLabel(side, ctx)} (Draw No Bet)`;
  }

  // ─── Double-chance (full match) ──────────────────────────────
  if ((m = mk.match(/^double_chance_(home_or_draw|home_or_away|away_or_draw)$/))) {
    if (m[1] === "home_or_draw") return `${sideLabel("home", ctx)} or Draw`;
    if (m[1] === "home_or_away") return `${sideLabel("home", ctx)} or ${sideLabel("away", ctx)}`;
    if (m[1] === "away_or_draw") return `Draw or ${sideLabel("away", ctx)}`;
  }
  if (mk === "double_chance") {
    const pair = dcPair(pk, ctx);
    if (pair) return pair;
  }

  // ─── Asian handicap, "pick" carries (side, sign, line) ───────
  // pk forms: home_plus_0_5 / away_minus_1 / home_plus_1_5
  if (mk === "asian_handicap" || mk === "ah") {
    if ((m = pk.match(/^(home|away)_(plus|minus)_([0-9_]+)$/))) {
      const side = m[1] as "home" | "away";
      const team = teamFor(side, ctx) ?? (side === "home" ? "Home" : "Away");
      const sign = m[2] === "minus" ? "-" : "+";
      return `${team} ${sign}${dec(m[3]!)} (AH)`;
    }
  }
  // Canonical asian_handicap_{side}_{sign}_{line}
  if ((m = mk.match(/^asian_handicap_(home|away)_(minus|plus)_([0-9_]+)$/))) {
    const side = m[1] as "home" | "away";
    const team = teamFor(side, ctx) ?? (side === "home" ? "Home" : "Away");
    const sign = m[2] === "minus" ? "-" : "+";
    return `${team} ${sign}${dec(m[3]!)} (AH)`;
  }

  // ─── BTTS ────────────────────────────────────────────────────
  if (mk === "btts_yes") return "BTTS Yes";
  if (mk === "btts_no") return "BTTS No";
  if (mk === "btts" || mk === "both_teams_to_score") {
    if (pk === "yes") return "BTTS Yes";
    if (pk === "no") return "BTTS No";
  }

  // ─── 1X2 (canonical match_winner_*) ──────────────────────────
  if ((m = mk.match(/^match_winner_(home|draw|away)$/))) {
    const side = m[1] as "home" | "draw" | "away";
    if (side === "draw") return "Draw";
    return `${sideLabel(side, ctx)} Win`;
  }
  if (mk === "match_winner" || mk === "1x2" || mk === "result" || mk === "ml" || mk === "moneyline") {
    const side = sideToken(pk);
    if (side === "draw") return "Draw";
    if (side) return `${sideLabel(side, ctx)} Win`;
  }

  // ─── Total goals — handle in-house so "under_3_5" decodes correctly.
  // (formatLeg's extractLine() reads "3_5" as "3" because underscores aren't
  // decimal points, so we cannot delegate this branch.)
  if ((m = mk.match(/^total_goals_(over|under)_([0-9_]+)$/))) {
    return `${cap(m[1]!)} ${dec(m[2]!)} Goals`;
  }
  if (mk === "total_goals" || mk === "ou") {
    if ((m = pk.match(/^(over|under)_([0-9_]+)$/))) {
      return `${cap(m[1]!)} ${dec(m[2]!)} Goals`;
    }
    // Last-resort: extract a numeric line from the pick text.
    const lineMatch = pk.match(/([0-9]+(?:\.[0-9]+)?)/);
    const line = lineMatch ? lineMatch[1] : "2.5";
    const dir = pk.startsWith("over") ? "Over" : pk.startsWith("under") ? "Under" : cap(pk);
    return `${dir} ${line} Goals`;
  }

  // ─── Fallback — delegate, then normalise word case ───────────
  const fallback = formatLeg(market, pick, ctx);
  if (fallback && fallback !== "—") return fallback.replace(/\bgoals\b/, "Goals");
  return "—";
}

/**
 * Compact eyebrow label for the market family — shown ABOVE the headline
 * selection in the premium card layout. Always short, always all-caps in
 * the UI (CSS `text-transform: uppercase`).
 *
 *   marketEyebrow("total_goals_under_3_5") → "Total Goals · Under"
 *   marketEyebrow("first_half_total_goals_over_0_5") → "1st Half Goals · Over"
 *   marketEyebrow("asian_handicap") → "Asian Handicap"
 *   marketEyebrow("combo_home_or_btts_yes") → "Combo"
 *   marketEyebrow("to_qualify_home") → "To Qualify"
 */
export function marketEyebrow(market: string | null | undefined): string {
  const mk = String(market ?? "").trim().toLowerCase();
  if (!mk) return "Market";
  let m: RegExpMatchArray | null;

  if ((m = mk.match(/^total_goals_(over|under)_/))) return `Total Goals · ${cap(m[1]!)}`;
  if (mk === "total_goals" || mk === "ou") return "Total Goals";

  if ((m = mk.match(/^(first|second)_half_total_goals_(over|under)_/))) {
    return `${m[1] === "first" ? "1st" : "2nd"} Half Goals · ${cap(m[2]!)}`;
  }
  if (mk === "first_half_total_goals") return "1st Half Goals";
  if (mk === "second_half_total_goals") return "2nd Half Goals";

  if ((m = mk.match(/^(first|second)_half_winner/))) {
    return `${m[1] === "first" ? "1st" : "2nd"} Half Winner`;
  }
  if ((m = mk.match(/^(first|second)_half_dc/))) {
    return `${m[1] === "first" ? "1st" : "2nd"} Half Double-Chance`;
  }

  if (mk.startsWith("first_half_corners")) return "1st Half Corners";
  if (mk.startsWith("second_half_corners")) return "2nd Half Corners";
  if (mk.startsWith("home_corners")) return "Home Corners";
  if (mk.startsWith("away_corners")) return "Away Corners";
  if (mk.startsWith("total_corners")) return "Total Corners";

  if (mk.startsWith("both_halves")) return "Both Halves";

  if (mk.startsWith("team_total") || mk.startsWith("team_goals_")) return "Team Total Goals";

  if (mk.startsWith("goal_bounds")) return "Goal Range";

  if (mk.startsWith("draw_no_bet")) return "Draw No Bet";
  if (mk.startsWith("double_chance")) return "Double Chance";

  if (mk === "asian_handicap" || mk === "ah" || mk.startsWith("asian_handicap_")) {
    return "Asian Handicap";
  }

  if (mk === "btts" || mk === "btts_yes" || mk === "btts_no" || mk === "both_teams_to_score") {
    return "Both Teams to Score";
  }

  if (mk.startsWith("combo_1x2") || mk.startsWith("combo_") && mk.includes("_or_")) return "Combo";
  if (mk.startsWith("combo_")) return "Combo";

  if (mk.startsWith("to_qualify")) return "To Qualify";
  if (mk.startsWith("to_win_match")) return "Match Winner (incl. ET / Pens)";
  if (mk.startsWith("extra_time")) return "Extra Time";
  if (mk.startsWith("penalties")) return "Penalty Shootout";

  if (mk === "match_winner" || mk === "1x2" || mk === "result" || /^match_winner_/.test(mk)) {
    return "Match Winner";
  }

  // Last-ditch fallback — title-case the market name.
  return mk.replace(/_+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
